import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import NotificationItem from '../NotificationItem';

import avatar from '../../assets/images/logos/avatar.png';
import logoIcon from '../../assets/images/logos/ba-brand-icon-colored.png';
import messageAvatarIcon from '../../assets/images/pre/main.png';
import appNotificationIcon from '../../assets/images/pre/nof.svg';

interface BaoNotificationDropdownProps {
  setIsNotificationOpen: (isOpen: boolean) => void;
  notificationTab: 'all' | 'unread' | 'messages';
  setNotificationTab: (tab: 'all' | 'unread' | 'messages') => void;
  filteredNotifications: any[];
  markAllAsRead: () => void;
  handleNotificationClick: (notif: any) => void;
}

const BaoNotificationDropdown: React.FC<BaoNotificationDropdownProps> = ({
  setIsNotificationOpen,
  notificationTab,
  setNotificationTab,
  filteredNotifications,
  markAllAsRead,
  handleNotificationClick,
}) => {
  const navigate = useNavigate();

  return (
    <div
      className="fixed right-8 top-20 w-96 bg-white shadow-lg border border-gray-200 z-50 notification-dropdown"
      style={{
        borderRadius: '20px',
        maxHeight: '600px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div className="px-6 pt-5 pb-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: '#212121' }}>Notifications</h3>
          <Button
            type="button"
            unstyled
            onClick={() => setIsNotificationOpen(false)}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-6 border-b border-gray-200 relative">
          <Button
            type="button"
            unstyled
            onClick={() => setNotificationTab('all')}
            className="pb-2 font-normal transition-colors relative"
            style={{
              color: notificationTab === 'all' ? '#64B5F6' : '#BABABA',
              fontSize: '12px'
            }}
          >
            All
            {notificationTab === 'all' && (
              <div className="absolute bottom-0 h-0.5" style={{ backgroundColor: '#64B5F6', left: '-4px', right: '-4px' }} />
            )}
          </Button>
          <Button
            type="button"
            unstyled
            onClick={() => setNotificationTab('unread')}
            className="pb-2 font-normal transition-colors relative"
            style={{
              color: notificationTab === 'unread' ? '#64B5F6' : '#BABABA',
              fontSize: '12px'
            }}
          >
            Unreads
            {notificationTab === 'unread' && (
              <div className="absolute bottom-0 h-0.5" style={{ backgroundColor: '#64B5F6', left: '-4px', right: '-4px' }} />
            )}
          </Button>
          <Button
            type="button"
            unstyled
            onClick={() => setNotificationTab('messages')}
            className="pb-2 font-normal transition-colors relative"
            style={{
              color: notificationTab === 'messages' ? '#64B5F6' : '#BABABA',
              fontSize: '12px'
            }}
          >
            Messages
            {notificationTab === 'messages' && (
              <div className="absolute bottom-0 h-0.5" style={{ backgroundColor: '#64B5F6', left: '-4px', right: '-4px' }} />
            )}
          </Button>
        </div>
      </div>

      {/* Notification List */}
      <div
        className="flex-1"
        style={{
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <style>
          {`
          .notification-dropdown::-webkit-scrollbar {
            display: none;
          }
        `}
        </style>
        {/* Render notifications grouped by day */}
        {(() => {
          // Get unique days from notifications
          const days = Array.from(new Set(filteredNotifications.map(n => n.day || 'Other').filter(Boolean)));
          return days.map(day => {
            const dayNotifs = filteredNotifications.filter(n => (n.day || 'Other') === day);
            if (dayNotifs.length === 0) return null;

            return (
              <div key={day} className={day === 'Today' ? 'pt-3 pb-1' : 'pt-2 pb-2'}>
                <p className="text-xs font-medium mb-2 px-6" style={{ color: '#B0B0B0' }}>{day}</p>
                {dayNotifs.map((notif: any, idx: number) => (
                  <NotificationItem
                    key={notif.id || idx}
                    notification={notif}
                    variant="compact"
                    defaultAvatar={avatar}
                    logoIcon={logoIcon}
                    messageIcon={messageAvatarIcon}
                    appIcon={appNotificationIcon}
                    onClick={() => handleNotificationClick(notif)}
                    onViewMessage={() => handleNotificationClick(notif)}
                    showDividerAfter
                  />
                ))}
              </div>
            );
          });
        })()}
      </div>

      {/* Footer */}
      <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <Button type="button" unstyled onClick={markAllAsRead} className="text-xs hover:opacity-70 transition-opacity" style={{ color: '#939393' }}>
          Mark all as read
        </Button>
        <Button
          type="button"
          unstyled
          onClick={() => {
            navigate('/notifications');
            setIsNotificationOpen(false);
          }}
          className="text-xs flex items-center space-x-1 hover:opacity-70 transition-opacity"
          style={{ color: '#64B5F6' }}
        >
          <span>See all notifications</span>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

export default BaoNotificationDropdown;
